import React from 'react';
import { Controller, Control, UseFormSetValue } from 'react-hook-form';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControlLabel,
  Grid,
  GridSize,
  TextField,
  Typography
} from '@mui/material';
import DropdownSelect from '../DropdownSelect';
import MultiValueInput from '../MultiValueInput';
import MultiSelectWithAll from '../MultiSelectWithAll';
import FileUploadWithPreview from '../FileUploadWithPreview';
import { FormFieldSchema } from './types';
import Icons from '../icons';
import { canAccessField } from '../../utils/roleAccess';
import TextInputWithIcon from '../TextInputWithIcon';
import CustomDatePicker from '../DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import StyledCheckbox from '../../modules/common/styled/Checkbox';
import SelectAll from '../SelectAllField/SelectAllField';

interface FormFieldProps {
  field: FormFieldSchema;
  control: Control<any>;
  handleFileUpload?: (file: File, uploadFor: string) => Promise<string>;
  setValue?: UseFormSetValue<any>;
  userRole?: string;
}

const FormField = ({
  field: fieldSchema,
  control,
  handleFileUpload,
  setValue,
  userRole
}: FormFieldProps) => {
  const { name, label, rules, disabled, shouldRender, allowedRoles } =
    fieldSchema;

  const watch = control._getWatch;
  const formData = watch();

  if (allowedRoles && userRole && !canAccessField(userRole, allowedRoles)) {
    return null;
  }

  if (shouldRender && !shouldRender(formData)) {
    return null;
  }

  const getGridProps = (item: FormFieldSchema) => {
    let defaultGridProps: { xs: GridSize; sm?: GridSize } = { xs: 12 };

    switch (item.type) {
      case 'checkbox':
        defaultGridProps.sm = 4;
        break;

      case 'fileUpload':
      case 'array':
      case 'section':
        break;

      default:
        defaultGridProps.sm = 6;
        break;
    }

    return { ...defaultGridProps, ...item.gridProps };
  };

  const gridProps = getGridProps(fieldSchema);

  const renderTitleAndSubtitle = () =>
    fieldSchema.displayTitle ||
    (fieldSchema.displaySubtitle && (
      <Box mb={2}>
        {fieldSchema.displayTitle && (
          <Typography variant='h6' style={{ fontWeight: 'bold' }} gutterBottom>
            {fieldSchema.displayTitle}
          </Typography>
        )}
        {fieldSchema.displaySubtitle && (
          <Typography variant='subtitle2' color='textSecondary' gutterBottom>
            {fieldSchema.displaySubtitle}
          </Typography>
        )}
      </Box>
    ));

  if (fieldSchema.type === 'empty') {
    return <Grid size={gridProps} />;
  }

  return (
    <Grid size={gridProps}>
      {renderTitleAndSubtitle()}

      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState: { error } }) => {
          const { transformBeforeRender, transformAfterInput } = fieldSchema;
          let transformedField = { ...field };
          const isRequired = fieldSchema.required ?? true;

          if (transformBeforeRender) {
            transformedField.value = transformBeforeRender(field.value);
          }

          if (transformAfterInput) {
            transformedField.onChange = (eventOrValue: any) => {
              const newValue = eventOrValue?.target
                ? eventOrValue.target.value
                : eventOrValue;
              const parsedValue = transformAfterInput(newValue);
              field.onChange(parsedValue);
            };
          }
          switch (fieldSchema.type) {
            case 'labelInputIcon':
              if (fieldSchema.inputType === 'date') {
                const valueStr = transformedField.value as string;
                const dateValue: Dayjs | null = valueStr
                  ? dayjs(valueStr)
                  : null;
                return (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <CustomDatePicker
                      label={fieldSchema.label || ''}
                      value={dateValue}
                      onChange={(newValue: Dayjs | null) => {
                        const formatted = newValue
                          ? dayjs(newValue).format('YYYY-MM-DD')
                          : '';
                        transformedField.onChange(formatted);
                      }}
                      required={isRequired}
                      disabled={fieldSchema.disabled}
                      formControlMargin={'none'}
                      width={'100%'}
                    />
                  </LocalizationProvider>
                );
              }
              return (
                <TextInputWithIcon
                  label={fieldSchema.label}
                  placeholder={fieldSchema.placeholder}
                  id={fieldSchema.name}
                  startIcon={fieldSchema.startIcon}
                  endIcon={fieldSchema.endIcon}
                  type={fieldSchema.inputType}
                  disabled={fieldSchema.disabled}
                  required={isRequired}
                  field={transformedField}
                  helperText={error?.message}
                  error={!!error}
                />
              );

            case 'input':
              return (
                <TextField
                  {...transformedField}
                  fullWidth
                  label={label}
                  placeholder={fieldSchema.placeholder}
                  type={fieldSchema.inputType || 'text'}
                  disabled={disabled}
                  required={isRequired}
                  error={!!error}
                  helperText={error?.message || ''}
                  variant='outlined'
                />
              );

            case 'checkbox':
              return (
                <FormControlLabel
                  title={fieldSchema.placeholder}
                  label={label}
                  control={
                    <StyledCheckbox
                      {...field}
                      checked={!!field.value}
                      disabled={disabled}
                    />
                  }
                />
              );

            case 'fileUpload':
              if (!handleFileUpload) {
                throw new Error(
                  'handleFileUpload is not provided for file componentType'
                );
              }
              return (
                <FileUploadWithPreview
                  title={fieldSchema.title}
                  name={name}
                  value={field.value}
                  uploadHandler={handleFileUpload}
                  onChange={field.onChange}
                  onRemove={() => field.onChange(null)}
                  disabled={disabled}
                  uploadFor={fieldSchema.uploadFor}
                />
              );

            case 'multiSelect':
              return (
                <MultiSelectWithAll
                  label={label}
                  placeholder={fieldSchema.placeholder}
                  options={fieldSchema.options}
                  selected={field.value || []}
                  onChange={field.onChange}
                  disabled={disabled}
                  hasTopLabel={fieldSchema.hasTopLabel}
                  required={isRequired}
                  formControlMargin='none'
                />
              );

            case 'multiSelectAll':
              return (
                <SelectAll
                  label={label}
                  placeholder={fieldSchema.placeholder}
                  options={fieldSchema.options}
                  selected={field.value || []}
                  onChange={field.onChange}
                  disabled={disabled}
                  hasTopLabel={fieldSchema.hasTopLabel}
                  required={isRequired}
                  formControlMargin='none'
                />
              )

            case 'multi-value-input':
              return (
                <MultiValueInput
                  label={label}
                  placeholder={fieldSchema.placeholder}
                  values={field.value || []}
                  onAddValue={(newValue) =>
                    field.onChange([...(field.value || []), newValue])
                  }
                  onRemoveValue={(indexToRemove) =>
                    field.onChange(
                      (field.value || []).filter(
                        (_: any, i: number) => i !== indexToRemove
                      )
                    )
                  }
                  validationType={fieldSchema.validationType}
                  disabled={disabled}
                  error={!!error}
                  helperText={error?.message || ''}
                  required={isRequired}
                />
              );

            case 'dropdown':
              return (
                <DropdownSelect
                  {...field}
                  label={label}
                  options={fieldSchema.options}
                  disabled={disabled}
                  hasTopLabel={fieldSchema.hasTopLabel}
                  placeholder={fieldSchema.placeholder}
                  required={isRequired}
                  fullWidth
                />
              );

            case 'section':
              return (
                <Accordion>
                  <AccordionSummary
                    expandIcon={
                      <span style={{ fontSize: 'small' }}>
                        <Icons.CaretDown />
                      </span>
                    }
                  >
                    <Typography>{fieldSchema.label}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={1}>
                      {fieldSchema.children.map((child, index) => {
                        const indexedChildName = `${fieldSchema.name}.${child.name}`;
                        return (
                          <FormField
                            control={control}
                            field={{ ...child, name: indexedChildName }}
                            handleFileUpload={handleFileUpload}
                            key={indexedChildName}
                            setValue={setValue}
                            userRole={userRole}
                          />
                        );
                      })}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );

            case 'array':
              if (!setValue) {
                throw new Error(
                  'setValue is not provided for array componentType'
                );
              }
              const currentArray = Array.isArray(field.value)
                ? field.value
                : [];
              return (
                <>
                  {currentArray.map((arrayItem: any, index: number) => (
                    <Accordion
                      key={arrayItem.id || index} // Use index as fallback key
                      style={{ marginBottom: '16px' }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <span style={{ fontSize: 'small' }}>
                            <Icons.CaretDown />
                          </span>
                        }
                      >
                        <Box
                          display='flex'
                          justifyContent='space-between'
                          alignItems='center'
                          width='100%'
                          marginRight='16px'
                        >
                          <Typography variant='h6'>
                            {arrayItem?.name || `Item ${index + 1}`}
                          </Typography>
                          <Button
                            variant='outlined'
                            onClick={(e: any) => {
                              e.stopPropagation();
                              const updatedArray = currentArray.filter(
                                (_: any, i: number) => i !== index
                              );
                              field.onChange(updatedArray); // Use field.onChange instead of setValue
                            }}
                          >
                            Remove
                          </Button>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={1}>
                          {fieldSchema.children.map((child) => {
                            const indexedChildName = `${name}[${index}].${child.name}`;
                            return (
                              <FormField
                                control={control}
                                field={{ ...child, name: indexedChildName }}
                                handleFileUpload={handleFileUpload}
                                key={indexedChildName}
                                setValue={setValue}
                                userRole={userRole}
                              />
                            );
                          })}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={() => {
                      const newItem =
                        fieldSchema.itemTemplate !== undefined
                          ? { ...fieldSchema.itemTemplate }
                          : {};

                      field.onChange([...formData[name], newItem]);
                    }}
                  >
                    Add {fieldSchema.label}
                  </Button>
                </>
              );
            default:
              throw new Error(`Unhandled field type: ${field.name}`);
          }
        }}
      />
      {fieldSchema.helperText && (
        <Typography variant='subtitle2' color='textSecondary' gutterBottom>
          {fieldSchema.helperText}
        </Typography>
      )}
    </Grid>
  );
};

export default FormField;
